/**
 * Time handling per docs/02 ("`timestamptz` UTC; `date` for calendar facts") and
 * CLAUDE.md ("All timestamps UTC in storage, localized at render, America/
 * New_York default").
 *
 * Display formats are fixed by docs/04 §7, which lists "mixed date formats" as a
 * prohibited anti-pattern: absolute dates render as `Aug 21, 2026`, and anything
 * under 24 hours old renders relative ("2h ago").
 */

/**
 * Where TKC operates, and the fallback when a device will not say where it is.
 *
 * Storage is always UTC (`timestamptz`); this is a RENDER concern only.
 */
export const FALLBACK_TIME_ZONE = 'America/New_York';

/**
 * The timezone to render in: the viewer's own device (ADR-39).
 *
 * Matt: "dates/time stamps should always be displayed in the users device's
 * timezone but should be actually recorded in UTC so that it is accurately
 * recorded and displayed across timezones."
 *
 * This was hard-coded to America/New_York, which is right for everyone at TKC
 * today and silently wrong for the first person who travels or works from
 * another state — an audit entry would claim a time they did not experience.
 * Resolved once at module load because it cannot change mid-session, and
 * wrapped because `resolvedOptions()` throws in a few locked-down environments
 * and a formatting helper must never be the thing that breaks a page.
 */
export const DISPLAY_TIME_ZONE = ((): string => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIME_ZONE;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
})();

/** A calendar-only fact (lease commencement, expiration) as `YYYY-MM-DD`. */
export type CalendarDate = string;

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

export class DateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DateError';
  }
}

export function assertCalendarDate(value: string): CalendarDate {
  if (!CALENDAR_DATE.test(value)) {
    throw new DateError(`Expected a YYYY-MM-DD calendar date, received "${value}"`);
  }
  return value;
}

/**
 * Formats an instant as `Aug 21, 2026` in the display timezone.
 *
 * Note the timezone matters for correctness, not just presentation: an event
 * stored at 2026-08-22T01:00Z happened on Aug 21 in Charlotte, and the audit
 * ledger must read the way the person who caused it remembers it.
 */
export function formatDate(instant: Date, timeZone: string = DISPLAY_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone,
  }).format(instant);
}

/** Formats an instant as `Aug 21, 2026 at 3:04 PM` in the display timezone. */
export function formatDateTime(instant: Date, timeZone: string = DISPLAY_TIME_ZONE): string {
  const date = formatDate(instant, timeZone);
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(instant);
  return `${date} at ${time}`;
}

/**
 * Formats a calendar date (no instant, no timezone conversion) as `Aug 21, 2026`.
 * Lease dates are calendar facts — shifting them by a timezone offset is a bug.
 */
export function formatCalendarDate(value: CalendarDate): string {
  assertCalendarDate(value);
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * Relative time for the last 24 hours, absolute date beyond that (docs/04 §7).
 * `now` is a required parameter rather than an implicit `Date.now()` so callers
 * are testable and server-rendered output is deterministic.
 */
export function formatRelative(
  instant: Date,
  now: Date,
  timeZone: string = DISPLAY_TIME_ZONE,
): string {
  const elapsed = now.getTime() - instant.getTime();

  if (elapsed < 0) return formatDate(instant, timeZone);
  if (elapsed < MINUTE_MS) return 'just now';
  if (elapsed < HOUR_MS) return `${String(Math.floor(elapsed / MINUTE_MS))}m ago`;
  if (elapsed < DAY_MS) return `${String(Math.floor(elapsed / HOUR_MS))}h ago`;
  return formatDate(instant, timeZone);
}

/*
 * There is no `EXPIRING_SOON_DAYS` here any more, and that is the point.
 *
 * It was one constant meaning "soon" for everything, with a comment promising
 * that per-kind windows would become "a parameter, not a second constant" —
 * after which `LEASE_EXPIRING_SOON_DAYS` became exactly that second constant.
 * Matt answered O-27 per-kind on 2026-08-27 (COI 30, agreement 90, utility
 * contract 90, lease 180), so both constants are gone and
 * `EXPIRATION_WINDOW_DAYS` in `contracts/expirations.ts` is the only place a
 * window is written down. Read it by kind.
 *
 * `expiryStatus` below therefore takes `windowDays` as a REQUIRED argument. A
 * default would have been friendlier and wrong: ten of this function's twelve
 * call sites relied on the old default, five of them were agreements, and
 * agreements just moved from 30 days to 90 — with a default those five would
 * have gone on quietly saying 30 and the compiler would not have said a word.
 * Requiring the argument is what made this change checkable.
 */

/** Today as a calendar date in the display timezone — not UTC, which rolls over first. */
export function todayCalendarDate(timeZone: string = DISPLAY_TIME_ZONE): CalendarDate {
  // en-CA formats as YYYY-MM-DD, which is the shape we store.
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(new Date());
}

/**
 * Where a calendar-dated expiration stands, as one of the docs/04 §4 statuses.
 *
 * Calendar arithmetic, deliberately: an expiration is a date on a contract, so
 * comparing it against an instant would make a COI expire at a different moment
 * for a reader in Denver than for one in Charlotte.
 */
export function expiryStatus(
  expiration: CalendarDate,
  today: CalendarDate,
  windowDays: number,
): 'expired' | 'expiring' | 'valid' {
  assertCalendarDate(expiration);
  assertCalendarDate(today);
  if (expiration < today) return 'expired';
  const limit = new Date(`${today}T00:00:00Z`);
  limit.setUTCDate(limit.getUTCDate() + windowDays);
  return expiration <= limit.toISOString().slice(0, 10) ? 'expiring' : 'valid';
}
